import { useNavigate } from "react-router-dom";
import { IconClipboardCheck, IconFileDescription } from "@tabler/icons-react";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { ChangeRequestDto } from "../../../../api";
import { humanizeTypeForAdmin } from "../../../change-requests/data/change-request-type";
import { useAdminDashboardChangeRequests } from "../../hooks/useAdminDashboardChangeRequests";
import { DashboardSectionCard } from "./dashboard-section-card";

type Item = Pick<
  ChangeRequestDto,
  "id" | "type" | "status" | "userName" | "userEmail" | "userId"
>;

function ChangeRequestItem({ row, onClick }: { row: Item; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-borderDefault bg-bgWhite px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bgLight text-textMuted">
        <IconFileDescription size={20} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodySmBold" tone="dark">
          {humanizeTypeForAdmin(row.type)}
        </Text>
        <Text variant="bodyXs" tone="muted">
          {row.userName || row.userEmail || row.userId.slice(0, 8)}
        </Text>
      </div>
      <Chip variant="inReview">Pending</Chip>
    </button>
  );
}

export function PendingChangeRequestsCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminDashboardChangeRequests();
  const items = ((data?.data as Item[] | undefined) ?? []).slice(0, 3);

  return (
    <DashboardSectionCard
      title="Pending Change Requests"
      viewAllRoute={AppRoutes.ADMIN_CHANGE_REQUESTS}
    >
      {isLoading ? (
        <ShimmerLoader message="Loading change requests…" lines={3} />
      ) : items.length === 0 ? (
        <EmptyState
          size="sm"
          icon={IconClipboardCheck}
          title="All caught up"
          description="No pending change requests waiting for review."
        />
      ) : (
        items.map((row) => (
          <ChangeRequestItem
            key={row.id}
            row={row}
            onClick={() =>
              navigate(AppRoutes.ADMIN_CHANGE_REQUEST_REVIEW.replace(":id", row.id))
            }
          />
        ))
      )}
    </DashboardSectionCard>
  );
}
